import classNames from 'classnames'
import type { ChangeEvent, ComponentProps } from 'react'
import React, { forwardRef, useEffect, useId, useRef } from 'react'
import { BreakoutTooltip } from './BreakoutTooltip'

interface SwitchProps extends ComponentProps<'input'> {
  label?: string | React.ReactNode
  labelPosition?: 'left' | 'right'
  labelClassName?: string
  className?: string
  disabled?: boolean
  initialValue?: boolean
  testId?: string
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  containerProps?: React.HTMLAttributes<HTMLDivElement>
  labelProps?: React.HTMLAttributes<HTMLLabelElement>
  circleProps?: React.HTMLAttributes<HTMLLabelElement>
  inputRef?: React.Ref<HTMLInputElement>
  accent?: boolean
  tooltip?: string
}

export const BreakoutSwitch = forwardRef<HTMLInputElement, SwitchProps>(
  function BreakoutSwitch(
    {
      label,
      labelPosition = 'right',
      disabled,
      initialValue,
      onChange,
      testId,
      className,
      containerProps,
      circleProps,
      labelProps,
      accent = false,
      tooltip,
      labelClassName,
      ...rest
    },
    ref
  ) {
    const switchId = useId()
    const internalInputRef = useRef<HTMLInputElement | null>(null)

    disabled = disabled ?? false

    const labelComponent = (
      <label
        {...labelProps}
        htmlFor={rest.id || switchId}
        className={classNames(
          'select-none px-4 text-body-medium text-on-surface-var',
          {
            'cursor-pointer': !disabled,
            'cursor-not-allowed': disabled,
          },
          labelClassName,
          labelProps?.className
        )}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {label}
      </label>
    )

    useEffect(() => {
      if (internalInputRef.current) {
        if (initialValue !== undefined) {
          internalInputRef.current.checked = initialValue
        }
      }
    }, [internalInputRef, initialValue])

    const handleToggle = () => {
      if (disabled) return
      internalInputRef.current?.click()
    }

    return (
      <div
        ref={ref}
        className={classNames(
          'inline-flex items-center',
          {
            'opacity-50': disabled,
            'cursor-pointer': !disabled,
            'cursor-not-allowed': disabled,
          },
          className
        )}
        onMouseDown={handleToggle}
      >
        {label && labelPosition === 'left' && labelComponent}
        <BreakoutTooltip content={tooltip || ''} enabled={!!tooltip}>
          <div
            {...containerProps}
            className={classNames(
              'relative inline-block h-4 w-8 rounded-full',
              {
                'cursor-pointer': !disabled,
                'cursor-not-allowed': disabled,
              },
              containerProps?.className
            )}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <input
              {...rest}
              ref={(ref) => {
                internalInputRef.current = ref
              }}
              type="checkbox"
              disabled={disabled}
              id={rest.id || switchId}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleToggle()
                }
              }}
              className={classNames(
                "before:content[''] peer absolute h-4 w-8 appearance-none rounded-full bg-surface-dim transition-colors duration-300 before:absolute before:-top-1 before:left-2 before:block before:h-6 before:w-6 checked:before:left-0 peer-checked:border-gray-900",
                {
                  'checked:bg-core-primary focus:outline-none focus:ring-2 focus:ring-on-surface-disabled peer-checked:before:bg-core-primary':
                    !accent,
                  'checked:bg-fixed-accent-color peer-checked:before:bg-fixed-accent-color':
                    accent,
                  'cursor-pointer': !disabled,
                  'cursor-not-allowed': disabled,
                  'opacity-50': disabled,
                }
              )}
              onChange={onChange}
            />
            <label
              data-testid={testId ? testId : undefined}
              {...circleProps}
              htmlFor={rest.id || switchId}
              className={classNames(
                "border-blue-gray-100 before:content[''] before:bg-blue-gray-500 before:pointer-events absolute -left-1 top-2/4 h-5 w-5 -translate-y-2/4 rounded-full border bg-core-tertiary shadow-md transition-all duration-300 before:absolute before:left-2/4 before:top-2/4 before:block before:h-10 before:w-10 before:-translate-x-2/4 before:-translate-y-2/4 before:rounded-full before:opacity-0 before:transition-opacity peer-checked:translate-x-full",
                {
                  'peer-checked:border-gray-900 peer-checked:before:bg-core-primary':
                    !accent,
                  'peer-checked:border-fixed-accent-color peer-checked:before:bg-fixed-accent-color':
                    accent,
                  'cursor-pointer hover:before:opacity-10': !disabled,
                  'cursor-not-allowed': disabled,
                },
                circleProps?.className
              )}
            ></label>
          </div>
        </BreakoutTooltip>
        {label && labelPosition === 'right' && labelComponent}
      </div>
    )
  }
)
